import * as React from 'react';
import { useContext, useLayoutEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router';
import LoadingScreen from '../components/muiLegacy/LoadingScreen/LoadingScreen';
import NotFoundErrorPage from '../logic/errorHandling/NotFoundErrorPage';
import StandardErrorPage from '../logic/errorHandling/StandardErrorPage';
import TeamMismatchPage from '../logic/errorHandling/TeamMismatchPage';
import { AuthenticationContext } from '../logic/security/authentication/AuthenticationContext';
import AuthCallbackPage from './AuthCallbackPage/AuthCallbackPage';
import ConnectionInvitationPage from './ConnectionInvitationPage/ConnectionInvitationPage';
import ProjectPage from './ProjectPage/ProjectPage';
import QuickSearchPageRouter from './QuickSearchPage/QuickSearchPageRouter';
import RegisterTeam from './SelfSignUpPage/RegisterTeam';
import RegisterTeamMembers from './SelfSignUpPage/RegisterTeamMembers';
import SelfSignUpPage from './SelfSignUpPage/SelfSignUpPage';
import SignUpSuccess from './SelfSignUpPage/SignUppSuccess';
import SignInPage from './SignInPage/SignInPage';
import StartPage from './StartPage/StartPage';
import Storybook from './Storybook/Storybook';
import StripeCheckoutCanceled from './StripeCheckout/StripeCheckoutCanceled';
import StripeCheckoutSuccess from './StripeCheckout/StripeCheckoutSuccess';
import TeamPage from './TeamPage/TeamPage';
import UnsubscribePage from './UnsubscribePage/UnsubscribePage';
import UserPage from './UserPage/UserPage';
import useScrollToAnchor from '../logic/otherUtilities/useScrollToAnchor';

export interface RootRouterProps {}

const RootRouter: React.FC<RootRouterProps> = ({}) => {
  useScrollToAnchor();

  // Authentication
  const authContext = useContext(AuthenticationContext);

  const location = useLocation();
  // Scroll to top if path changes
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Rendering
  if (authContext.initializing) {
    return <LoadingScreen />;
  }

  if (!authContext.authenticated) {
    return (
      <Switch>
        <Route path={'/callback'} render={() => <AuthCallbackPage />} />
        <Route path={'/register/:token?'} render={() => <SelfSignUpPage />} />
        <Route path={`/t/:token`} render={(renderProps) => <SelfSignUpPage tokenVersion />} />
        <Route path={'/'} render={() => <SignInPage />} />
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path={'/team-mismatch'} render={() => <TeamMismatchPage />} />
      <Route path={'/register/members'} render={() => <RegisterTeamMembers />} />
      <Route path={'/register/success'} render={() => <SignUpSuccess />} />
      <Route
        path={'/promo/:promoCode?'}
        render={(subProps) => <RegisterTeam promoCode={subProps?.match?.params?.promoCode} />}
      />
      <Route
        path={'/register/:token?'}
        render={(subProps) => <RegisterTeam token={subProps?.match?.params?.token} />}
      />

      {!!window.location.host.includes('localhost') && (
        <Route path="/storybook" component={Storybook} />
      )}

      <Route
        path={'/checkout/success/:session_id'}
        render={(subProps) => (
          <StripeCheckoutSuccess sessionId={subProps.match.params.session_id} />
        )}
      />
      <Route path={'/checkout/canceled'} render={() => <StripeCheckoutCanceled />} />

      <Route path={`/user/:tab`} render={() => <UserPage />} />
      <Route path={`/team/:tab`} render={() => <TeamPage />} />
      <Route
        path={`/t/:token`}
        render={(renderProps) => (
          <ConnectionInvitationPage token={renderProps.match.params.token} />
        )}
      />
      <Route path={'/quick-search'} render={() => <QuickSearchPageRouter />} />
      <Route
        path={'/:id(\\d+)'}
        render={(renderProps) => <ProjectPage id={+renderProps.match.params.id} />}
      />
      <Route path={'/error'} render={() => <StandardErrorPage />} />
      <Route path={'/unsubscribe'} render={() => <UnsubscribePage />} />
      <Route path={'/callback'} render={() => <StartPage />} />
      <Route path={'/:unknown'} render={() => <NotFoundErrorPage />} />
      <Route path={''} render={() => <StartPage />} />
      <Route path={'/'} render={() => <StartPage />} />
    </Switch>
  );
};

export default RootRouter;
